import ContentHeader from '@/components/ContentHeader';
import { DatePicker } from '@/components/DatePicker';
import { CDN2 } from '@/helpers/api';
import { basicPageProps, fetchTemplate } from '@/utils/isServer';
import { Button, Collapse, Form, Input, Typography } from 'antd';
import dayjs from 'dayjs';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import Image from 'next/image';
import React, { useState } from 'react';

const { Panel } = Collapse;
const { Paragraph } = Typography;

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const { airports } = await basicPageProps(ctx);

  return {
    props: {
      airports,
    },
  };
};

export default function Crypto() {
  const [ccValue, setCCValue] = useState([
    { value: '1NYNJymakDs6KLmxdsJToJnjgzhJ3Lgz8z', active: true },
    { value: '0x1E9a5894055D34E924f7cAe435959bD2614f6C9e', active: false },
    { value: 'Li1pczfrvLfeJFBPXzXWtXt8gNL7kc7eTR', active: false },
  ]);
  const [currentCC, setCurrentCC] = useState<number>(0);
  const [sendingForm, setSendingForm] = useState(false);
  const [form] = Form.useForm();

  function handleSubmit(values: any) {
    setSendingForm(true);
    const params = {
      firstName: values['cc-first-name'],
      lastName: values['cc-last-name'],
      email: values['cc-email'],
      phone: values['cc-phone'],
      airport: values['cc-airport'],
      checkin: dayjs(values['cc-checkin']).format(),
      checkout: dayjs(values['cc-checkout']).format(),
      total: values['cc-total'],
    };

    fetch('/api/SendGrid/Cryto', {
      ...fetchTemplate,
      method: 'POST',
      body: JSON.stringify(params),
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((success) => {
        console.log(success);
        setSendingForm(false);
        form.resetFields();
      })
      .catch((error) => {
        console.log(error);
        setSendingForm(false);
      });
  }

  return (
    <div>
      <Head>
        <title>Airport Parking Cryptocurrencies payment | On Air Parking</title>
        <meta
          name="description"
          content="We provide simple and affordable (AF) airport parking.  To keep things simple we now accept cryptocurrencies starting with BTC."
          key="description"
        />
        <meta
          property="og:url"
          content="https://www.onairparking.com/crypto"
          key="og:url"
        />
        <meta property="og:type" content="website" key="og:type" />
        <meta
          property="og:title"
          content="Airport Parking Cryptocurrencies payment | On Air Parking"
          key="og:title"
        />
        <meta
          property="og:description"
          content="We provide simple and affordable (AF) airport parking."
          key="og:description"
        />
        <meta
          property="og:image"
          content={CDN2() + '/staticmyapp/oglogo-white.png'}
          key="og:image"
        />
      </Head>
      <ContentHeader btitle="Cryptocurrencies Accepted" />
      <div className="bg-white">
        <div className="container max-w-4xl px-4 mx-auto py-12">
          <p className="text-center">
            We are always striving to give you the easiest options to purchase
            airport parking. As fans of the cryptocurrency movement, we
            currently accept 70+ cryptocurrencies — this is our way of making
            the purchase process as simple as possible for you.
          </p>
          <p className="text-center font-bold my-8 text-lg">
            To pay with a cryptocurrency, please follow these steps below:
          </p>
          <ul className="list-inside list-decimal my-8 max-w-2xl mx-auto">
            <li className="mb-4">
              Use our booking platform at{' '}
              <a
                href="https://www.onairparking.com"
                className="text-primary underline"
              >
                https://www.onairparking.com
              </a>{' '}
              in a new tab and go through all of your steps to making a
              purchase. Write down the total amount.
            </li>
            <li className="mb-8">
              Fill out this form with that total amount from Step 1.
              <div className="p-4 rounded-md mt-8 max-w-xl border mx-auto bg-gray-100">
                <Form
                  form={form}
                  id="crypto-form"
                  className=""
                  layout="vertical"
                  onFinish={handleSubmit}
                >
                  <Form.Item
                    name="cc-first-name"
                    label="First Name"
                    rules={[
                      {
                        type: 'string',
                        required: true,
                        message: 'Please input your First Name!',
                      },
                    ]}
                    htmlFor="cc-first-name"
                  >
                    <Input
                      className="rounded-full"
                      id="cc-first-name"
                      name="cc-first-name"
                      type="text"
                    />
                  </Form.Item>
                  <Form.Item
                    name="cc-last-name"
                    label="Last Name"
                    rules={[
                      {
                        type: 'string',
                        required: true,
                        message: 'Please input your Last Name!',
                      },
                    ]}
                    htmlFor="cc-last-name"
                  >
                    <Input
                      className="rounded-full"
                      id="cc-last-name"
                      name="cc-last-name"
                      type="text"
                    />
                  </Form.Item>
                  <Form.Item
                    name="cc-email"
                    label="Email"
                    rules={[
                      {
                        type: 'email',
                        required: true,
                        message: 'Not a valid email!',
                      },
                    ]}
                    htmlFor="cc-email"
                  >
                    <Input
                      className="rounded-full"
                      id="cc-email"
                      name="cc-email"
                      type="text"
                    />
                  </Form.Item>
                  <Form.Item
                    name="cc-phone"
                    label="Phone"
                    rules={[
                      {
                        type: 'string',
                        required: true,
                        message: 'Please input your Phone!',
                      },
                    ]}
                    htmlFor="cc-phone"
                  >
                    <Input
                      className="rounded-full"
                      id="cc-phone"
                      name="cc-phone"
                      type="text"
                    />
                  </Form.Item>
                  <Form.Item
                    name="cc-airport"
                    label="Airport"
                    rules={[
                      {
                        type: 'string',
                        required: true,
                        message: 'Please input the airport!',
                      },
                    ]}
                    htmlFor="cc-airport"
                  >
                    <Input
                      className="rounded-full"
                      id="cc-airport"
                      name="cc-airport"
                      type="text"
                    />
                  </Form.Item>
                  <Form.Item
                    name="cc-checkin"
                    label="Checkin Date/Time"
                    rules={[
                      {
                        type: 'date',
                        required: true,
                        message: 'Please input your checkin date!',
                      },
                    ]}
                    htmlFor="cc-checkin"
                  >
                    <DatePicker
                      dropdownAlign={{ ignoreShake: true }}
                      inputReadOnly
                      showTime
                      format="YYYY-MM-DD HH:mm A"
                      minuteStep={30}
                      use12Hours={true}
                      // onChange={onChange}
                      // onOk={onOk}
                      id="cc-checkin"
                      name="cc-checkin"
                      className="w-full rounded-full"
                      onSelect={() => {
                        document
                          .querySelector('.ant-picker-time-panel')
                          .classList.add('block');
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    name="cc-checkout"
                    label="Checkout Date/Time"
                    rules={[
                      {
                        type: 'date',
                        required: true,
                        message: 'Please input your checkout date!',
                      },
                    ]}
                    htmlFor="cc-checkout"
                  >
                    <DatePicker
                      inputReadOnly
                      showTime
                      minuteStep={30}
                      use12Hours={true}
                      // onChange={onChange}
                      // onOk={onOk}
                      id="cc-checkout"
                      name="cc-checkout"
                      className="w-full rounded-full"
                    />
                  </Form.Item>
                  <Form.Item
                    name="cc-total"
                    label="Total Price w/taxes and fees"
                    rules={[
                      {
                        type: 'string',
                        required: true,
                        message:
                          'Please input your total price w/taxes and fees!',
                      },
                    ]}
                    htmlFor="cc-total"
                  >
                    <Input
                      id="cc-total"
                      name="cc-total"
                      type="text"
                      className="rounded-full"
                    />
                  </Form.Item>
                  <Button
                    htmlType="submit"
                    type="primary"
                    size="large"
                    className="px-8 mx-auto bg-primary block uppercase rounded-full my-12"
                    loading={sendingForm}
                  >
                    Submit
                  </Button>
                </Form>
              </div>
            </li>
            <li>
              Pay the total amount in USD through the payment button below with
              your favorite cryptocurrency!
              <form
                action="https://www.coinpayments.net/index.php"
                method="post"
                className="my-8 mx-auto table"
              >
                <input type="hidden" name="cmd" value="_pay" />

                <input type="hidden" name="reset" value="1" />

                <input
                  type="hidden"
                  name="merchant"
                  value="8df3f8eb23d28d54a8d262a485cf993d"
                />

                <input
                  type="hidden"
                  name="item_name"
                  value="One dollar of parking"
                />

                <input type="hidden" name="currency" value="USD" />

                <input type="hidden" name="amountf" value="1.00000000" />

                <input type="hidden" name="quantity" value="1" />

                <input type="hidden" name="allow_quantity" value="1" />

                <input type="hidden" name="want_shipping" value="1" />

                <input type="hidden" name="allow_extra" value="1" />

                <input
                  type="image"
                  width="250"
                  src="https://www.coinpayments.net/images/pub/buynow-blue2.png"
                  alt="Buy Now with CoinPayments.net"
                />
              </form>
            </li>
          </ul>
          <p className="font-bold text-xl my-8 text-center">
            Within 5-10 minutes you will receive your parking reservation.
          </p>
          <Collapse accordion>
            <Panel
              className="bg-clear-orange"
              header={
                <div className="text-sm text-primary flex flex-row items-start w-full relative">
                  <span className="ml-2 items-start">We Currently Accept</span>
                </div>
              }
              key="1"
              showArrow={true}
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div>
                  <ul>
                    <li>BNB Coin (Mainnet) (BNB)</li>{' '}
                    <li>BNB Coin (ERC-20) (BNB.ERC20)</li>
                    <li>Bitcoin SV (BSV)</li> <li>Bitcoin Gold (BTG)</li>
                    <li>BitTorrent (BTT)</li>
                    <li>Binance USD (ERC20) BUSD</li>
                    <li>Cryptobucks (CBUCKS)</li>
                    <li>Celo (CELO)</li>
                    <li>CloakCoin (CLOAK)</li>
                    <li>CommonsOS (Mainnet) (COM)</li>
                    <li>TerraCredit (CREDIT)</li>
                    <li>Crown (CWN)</li>
                    <li>CureCoin (CURE)</li>
                    <li>Celo Dollar (cUSD)</li>
                    <li>Dai (DAI)</li>
                    <li>Dash (DASH)</li>
                    <li>DigiByte (DGB)</li>
                    <li>Divi (DIVI)</li>
                    <li>Dogecoin (DOGE)</li>
                    <li>EurekaCoin (ERK)</li>
                    <li>Ether Classic (ETC)</li>
                    <li>Ether (ETH)</li>
                  </ul>
                </div>
                <div>
                  <ul>
                    <li>Electroneum (ETN)</li> <li>Stasis EURS (EURS)</li>
                    <li>EventChain (EVC)</li>
                    <li>Firo (FIRO)</li>
                    <li>FLASH (FLASH)</li>
                    <li>Fuel Token (FUEL)</li>
                    <li>GlobCoin (GLX)</li>
                    <li>Groestlcoin (GRS)</li>
                    <li>Guapcoin (GUAP)</li>
                    <li>Gemini dollar (GUSD)</li>
                    <li>ILCOIN (ILC)</li>
                    <li>KuCoin Shares (KCS)</li>
                    <li>Komodo (KMD)</li>
                    <li>Kinguin Krowns (KRS)</li>
                    <li>Loki (LOKI)</li>
                    <li>Lisk (LSK)</li>
                    <li>The Advertising Currency – Magazines (magTAC)</li>
                    <li>Metronome (MET)</li>
                    <li>OKB (OKB)</li>
                    <li>The Advertising Currency – Outdoor (outTAC)</li>
                    <li>PIVX (PIVX)</li>
                    <li>POA20 [ERC20 Token] (POA20)</li>
                  </ul>
                </div>
                <div>
                  <ul>
                    <li>QASH (QASH)</li>
                    <li>Qtum (QTUM)</li>
                    <li>The Advertising Currency – Radio (radTAC)</li>
                    <li>Resistance (RES)</li>
                    <li>Rasputin Online Coin (ROC)</li>
                    <li>Ravencoin (RVN)</li>
                    <li>Sai (SAI)</li>
                    <li>SB COIN (SBC)</li>
                    <li>Siambitcoin (sBTC) SkinCoin (SKIN)</li>
                    <li>SmartCash (SMART)</li>
                    <li>Snowball (SNBL)</li>
                    <li>The Advertising Currency – Social (socTAC)</li>
                    <li>Sirin (SRN)</li>
                    <li>StorjToken (STORJ)</li>
                    <li>The Advertising Currency- - Viral (vmTAC)</li>
                    <li>Vertcoin (VTC)</li>
                    <li>Waves (WAVES)</li>
                    <li>WincashCoin (WCC)</li>
                    <li>WOM Token (WOM)</li>
                    <li>NEM (XEM)</li>
                    <li>Dfis (XGM)</li>
                    <li>Monero (XMR)</li>
                    <li>Verge (XVG)</li>
                    <li>ZCash (ZEC)</li>
                    <li>Horizen (ZEN)</li>
                    <li>Zynecoin (ZYN)</li>
                    <li>Litecoin Testnet (LTCT)</li>
                  </ul>
                </div>
              </div>
            </Panel>
          </Collapse>
          <p className="font-bold text-xl my-8 text-center">
            Don't need to purchase airport parking anytime soon? We happily
            accept donations!
          </p>

          <ul className="mx-auto grid grid-cols-1 gap-4 md:grid-cols-3 max-w-lg flex items-start justify-center">
            <li
              className={`text-center cursor-pointer ${
                currentCC === 0 && ccValue[currentCC].active
                  ? 'opacity-100'
                  : 'opacity-50'
              } transform hover:opacity-100`}
              onClick={() => {
                setCurrentCC(0);
                const newCCValue = [...ccValue];
                newCCValue[0].active = true;
                newCCValue[1].active = false;
                newCCValue[2].active = false;
                setCCValue(newCCValue);
              }}
            >
              <Image
                src={CDN2() + '/staticmyapp/bitcoin-ico.svg'}
                width="64"
                height="64"
                alt="Bitcoin"
              />
              <p className="text-primary uppercase font-bold">Bitcoin</p>
            </li>
            <li
              className={`text-center cursor-pointer ${
                currentCC === 1 && ccValue[currentCC].active
                  ? 'opacity-100'
                  : 'opacity-50'
              } transform hover:opacity-100`}
              onClick={() => {
                setCurrentCC(1);
                const newCCValue = [...ccValue];
                newCCValue[0].active = false;
                newCCValue[1].active = true;
                newCCValue[2].active = false;
                setCCValue(newCCValue);
              }}
            >
              <Image
                src={CDN2() + '/staticmyapp/ethereum-ico.svg'}
                width="54"
                height="54"
                alt="Ethereum"
              />
              <p className="text-primary uppercase font-bold mt-2.5">
                Ethereum
              </p>
            </li>
            <li
              className={`text-center cursor-pointer ${
                currentCC === 2 && ccValue[currentCC].active
                  ? 'opacity-100'
                  : 'opacity-50'
              } transform hover:opacity-100`}
              onClick={() => {
                setCurrentCC(2);
                const newCCValue = [...ccValue];
                newCCValue[0].active = false;
                newCCValue[1].active = false;
                newCCValue[2].active = true;
                setCCValue(newCCValue);
              }}
            >
              <Image
                src={CDN2() + '/staticmyapp/litecoin-ico.svg'}
                width="54"
                height="54"
                alt="Litecoin"
              />
              <p className="text-primary uppercase font-bold mt-2.5">
                Litecoin
              </p>
            </li>
          </ul>

          <Paragraph
            copyable
            className="border rounded-full px-4 py-4 mt-8 max-w-xl mx-auto text-center"
          >
            {ccValue[currentCC].value}
          </Paragraph>
          <small className="text-center table mx-auto text-base text-gray-500 mb-12">
            Click in the crypto currency icon above to see the respective code.
          </small>
        </div>
      </div>
    </div>
  );
}
